import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import TaxWeekMonthSelect from '@nxsys/components/AppTaxWeekMonthSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { showCurrencyByCountry } from '@nxsys/utils';
import { Form } from 'antd';
import React, { Ref, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';

export type OneOffBenefitFormValues = {
  value: string;
  description: string;
  taxPeriod: number;
  eeContribution: string;
  totalEeContribution: string;
};

export type OneOffBenefitFormRef = {
  submit: () => void;
  setFieldsValue: (values: Partial<OneOffBenefitFormValues>) => void;
};

type OneOffBenefitFormProps = {
  formRef?: Ref<OneOffBenefitFormRef>;
  onSubmit?: (values: OneOffBenefitFormValues) => void;
  defaultValues?: OneOffBenefitFormValues;
};

const OneOffBenefitForm: React.FC<OneOffBenefitFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
}) => {
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);

  const { messages } = useTypedIntl();

  const [form] = Form.useForm<OneOffBenefitFormValues>();

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
    setFieldsValue(values) {
      form.setFieldsValue(values);
    },
  }));

  const onFinish = (values: OneOffBenefitFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={defaultValues}
    >
      <Form.Item
        label={messages['common.description']}
        name="description"
        rules={[
          {
            required: true,
            message: messages['form.pleaseEnter'],
          },
        ]}
      >
        <AppInput placeholder={messages['form.enter']} />
      </Form.Item>
      <Form.Item
        label={messages['common.grossPremium']}
        name="value"
        rules={[
          {
            required: true,
            message: messages['form.pleaseEnter'],
          },
        ]}
      >
        <AppCurrencyInput
          placeholder={messages['form.enter']}
          prefix={showCurrencyByCountry(payrollCountry)}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.taxPeriod']}
        name="taxPeriod"
        rules={[
          {
            required: true,
            message: messages['form.pleaseSelect'],
          },
        ]}
      >
        <TaxWeekMonthSelect
          placeholder={messages['form.select']}
          onFilter={(value) => {
            form.setFieldsValue({ taxPeriod: value.taxPeriod });
          }}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.employeeContribution']}
        name="eeContribution"
      >
        <AppCurrencyInput
          placeholder={messages['form.enter']}
          prefix={showCurrencyByCountry(payrollCountry)}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.totalEmployeeContribution']}
        name="totalEeContribution"
      >
        <AppCurrencyInput
          placeholder={messages['form.enter']}
          prefix={showCurrencyByCountry(payrollCountry)}
          disabled
        />
      </Form.Item>
    </AppFormContainer>
  );
};

export default OneOffBenefitForm;
